/**
 * Navigation components for ThePedigreeApp
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Home,
  Cat,
  LayoutDashboard,
  FileText,
  DollarSign,
  Settings,
  LogOut,
  User,
  Search,
  ChevronDown,
  Heart,
  Crown,
  Receipt,
  Stethoscope,
  Baby,
  ShoppingBag,
  MessageSquare,
  Gift,
  Dna
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../utils/helpers';
import { Avatar, Button } from './UIComponents';

// Public navigation links
const publicLinks = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/marketplace', label: 'Marketplace', icon: Search },
  { href: '/breeds', label: 'Breeds', icon: Cat },
];

// Breeder dashboard links
const breederLinks = [
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/dashboard/enquiries', label: 'Enquiries', icon: MessageSquare },
  { href: '/dashboard/cats', label: 'My Cats', icon: Cat },
  { href: '/dashboard/breeding', label: 'Breeding', icon: Heart },
  { href: '/dashboard/litters', label: 'Litters & Kittens', icon: Baby },
  { href: '/dashboard/sales', label: 'Sales', icon: ShoppingBag },
  { href: '/dashboard/assets', label: 'Fixed Assets', icon: Crown },
  { href: '/dashboard/health', label: 'Health', icon: Stethoscope },
  { href: '/dashboard/finances', label: 'Finances', icon: Receipt },
  { href: '/dashboard/food-partners', label: 'Food Partners', icon: Gift },
  { href: '/dashboard/genetics', label: 'Genetics Predictor', icon: Dna },
  { href: '/dashboard/registrations', label: 'Registrations', icon: FileText },
  { href: '/dashboard/ledger', label: 'Commission', icon: DollarSign },
  { href: '/dashboard/settings', label: 'Settings', icon: Settings },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, isAuthenticated, isBreeder, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-feline-500 rounded-xl flex items-center justify-center">
                <Cat className="w-6 h-6 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-gray-900">
                The<span className="text-feline-500">Pedigree</span>App
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {publicLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'px-4 py-2 rounded-lg font-medium transition-colors',
                  isActive(link.href)
                    ? 'bg-feline-50 text-feline-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Avatar name={user?.displayName} size="sm" />
                  <span className="font-medium text-gray-700">{user?.displayName}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {userMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setUserMenuOpen(false)} 
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20">
                      {isBreeder && (
                        <>
                          {breederLinks.map((link) => (
                            <Link
                              key={link.href}
                              to={link.href}
                              onClick={() => setUserMenuOpen(false)}
                              className={cn(
                                'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                                isActive(link.href)
                                  ? 'bg-feline-50 text-feline-600'
                                  : 'text-gray-700 hover:bg-gray-50'
                              )}
                            >
                              <link.icon className="w-4 h-4" />
                              {link.label}
                            </Link>
                          ))}
                          <div className="border-t border-gray-200 my-2" />
                        </>
                      )}
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button>Register</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-2">
            {publicLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors',
                  isActive(link.href)
                    ? 'bg-feline-50 text-feline-600'
                    : 'text-gray-600 hover:bg-gray-50'
                )}
              >
                <link.icon className="w-5 h-5" />
                {link.label}
              </Link>
            ))}

            {isAuthenticated && isBreeder && (
              <>
                <div className="border-t border-gray-200 my-2 pt-2">
                  <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    Breeder Dashboard
                  </p>
                </div>
                {breederLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors',
                      isActive(link.href)
                        ? 'bg-feline-50 text-feline-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    <link.icon className="w-5 h-5" />
                    {link.label}
                  </Link>
                ))}
              </>
            )}

            <div className="border-t border-gray-200 my-2 pt-2">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-red-600 hover:bg-red-50 w-full"
                >
                  <LogOut className="w-5 h-5" />
                  Sign Out
                </button>
              ) : (
                <div className="space-y-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block"
                  >
                    <Button variant="outline" className="w-full">Sign In</Button>
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block"
                  >
                    <Button className="w-full">Register</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Footer component
export const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-feline-500 rounded-xl flex items-center justify-center">
                <Cat className="w-6 h-6 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-white">
                The<span className="text-feline-500">Pedigree</span>App
              </span>
            </div>
            <p className="text-sm max-w-md">
              South Africa's trusted platform for pedigree cat breeders. 
              Connect with verified breeders, register your cats, and find your perfect feline companion.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Platform</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/marketplace" className="hover:text-white transition-colors">Marketplace</Link></li>
              <li><Link to="/breeds" className="hover:text-white transition-colors">Cat Breeds</Link></li>
              <li><Link to="/register" className="hover:text-white transition-colors">For Breeders</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm">© {new Date().getFullYear()} ThePedigreeApp. All rights reserved.</p>
          <div className="flex items-center gap-4 text-sm">
            <span>Proudly South African</span>
            <span>🇿🇦</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
